import { useCallback, useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";

import { getMe, type Me } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState, Loading, PageHeader } from "@/components/Page";
import { initialsOf } from "@/components/UserMenu";
import { TwoFactorSection } from "@/components/TwoFactorSection";
import { cn } from "@/lib/utils";

const ROLE_CLS: Record<string, string> = {
  owner: "border-primary/30 bg-primary/10 text-primary",
  admin: "border-success/30 bg-success/10 text-success",
  editor: "border-warning/40 bg-warning/10 text-warning",
  viewer: "border-border bg-muted text-muted-foreground",
  member: "border-border bg-muted text-muted-foreground",
};
const roleLabel = (r: string) => (r === "member" ? "viewer" : r);

export function Profile() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    return getMe()
      .then(setMe)
      .catch((e) => setErr(humanizeError(e, "Couldn't load your profile")))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loading />;
  if (err || !me) return <ErrorState message={err ?? "Couldn't load your profile"} onRetry={load} />;

  const display = me.name || me.email || me.user_id || "Account";
  const workspaces = me.workspaces ?? [];

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title="Profile" description="Your account, workspaces, and security." />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
              {initialsOf(display)}
            </span>
            <div className="min-w-0">
              {me.name && <div className="truncate text-base font-medium">{me.name}</div>}
              {me.email && <div className="truncate text-sm text-muted-foreground">{me.email}</div>}
              <div className="mt-1">
                <Badge variant="outline" className="text-xs">
                  {me.auth === "api_key" ? "Signed in with an API key" : "Signed in"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Workspaces</CardTitle>
          <CardDescription>The workspaces you belong to and your role in each.</CardDescription>
        </CardHeader>
        <CardContent>
          {workspaces.length === 0 ? (
            <p className="text-sm text-muted-foreground">You're not a member of any workspace yet.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {workspaces.map((w) => {
                const isCurrent = w.tenant_id === me.current_tenant_id;
                return (
                  <li key={w.tenant_id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{w.name ?? w.tenant_id}</div>
                      {w.name && <div className="truncate font-mono text-xs text-muted-foreground">{w.tenant_id}</div>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {isCurrent && (
                        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                          Current
                        </Badge>
                      )}
                      <Badge variant="outline" className={cn("capitalize", ROLE_CLS[w.role] ?? ROLE_CLS.viewer)}>
                        {roleLabel(w.role)}
                      </Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Security
          </CardTitle>
          <CardDescription>How you sign in to CodeBridge.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div>
              <div className="font-medium">Passwordless sign-in</div>
              <p className="text-muted-foreground">
                Your account has no password — you sign in with a magic email link or with Google / GitHub.
              </p>
            </div>
          </div>
          {me.auth === "session" && <TwoFactorSection enabled={!!me.two_factor_enabled} onChanged={load} />}
        </CardContent>
      </Card>
    </div>
  );
}
